import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { isDateInFuture } from './utils';

interface TabBarProps {
    activeTab: number;
    setActiveTab: (tab: number) => void;
    sessionDays: string[];
}

const TabBar: React.FC<TabBarProps> = ({ activeTab, setActiveTab, sessionDays }) => {
    const scrollViewRef = useRef<ScrollView>(null);
    const [scrollPosition, setScrollPosition] = useState(0);

    const getTabDateLabel = (date: string) => {
        try {
            const parsedDate = new Date(date);
            if (isNaN(parsedDate.getTime())) return date;
            return parsedDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return date;
        }
    };

    const scrollToNextDay = () => {
        if (scrollViewRef.current) {
            const newPosition = scrollPosition + 100;
            scrollViewRef.current.scrollTo({ x: newPosition, y: 0, animated: true });
            setScrollPosition(newPosition);
        }
    };

    const scrollToPrevDay = () => {
        if (scrollViewRef.current) {
            const newPosition = Math.max(0, scrollPosition - 100);
            scrollViewRef.current.scrollTo({ x: newPosition, y: 0, animated: true });
            setScrollPosition(newPosition);
        }
    };

    return (
        <View style={styles.tabsContainerWrapper}>
            <View style={styles.tabsRow}>
                {/* Overview Tab */}
                <Pressable
                    style={[styles.overviewTab, activeTab === 0 && styles.activeOverviewTab]}
                    onPress={() => setActiveTab(0)}
                >
                    <MaterialCommunityIcons 
                        name="chart-box" 
                        size={18} 
                        color={activeTab === 0 ? '#2C67D6' : '#666'} 
                    />
                    <Text style={[styles.overviewTabText, activeTab === 0 && styles.activeOverviewTabText]}>
                        Overview
                    </Text>
                </Pressable>

                {/* Day Tabs with Scroll */}
                <View style={styles.dayTabsContainer}>
                    <Pressable style={styles.scrollArrow} onPress={scrollToPrevDay}>
                        <MaterialCommunityIcons name="chevron-left" size={18} color="#666" />
                    </Pressable>
                    
                    <ScrollView 
                        ref={scrollViewRef}
                        horizontal 
                        showsHorizontalScrollIndicator={false}
                        style={styles.dayTabsScrollView}
                        contentContainerStyle={styles.dayTabsContentContainer}
                    >
                        {sessionDays.map((date, index) => {
                            const isDisabled = isDateInFuture(date);
                            return (
                                <Pressable
                                    key={date}
                                    style={[
                                        styles.dayTab,
                                        activeTab === index + 1 && styles.activeDayTab,
                                        isDisabled && styles.disabledDayTab,
                                    ]}
                                    onPress={() => setActiveTab(index + 1)}
                                >
                                    <MaterialCommunityIcons 
                                        name="calendar-check" 
                                        size={16} 
                                        color={isDisabled ? '#bbb' : (activeTab === index + 1 ? '#2C67D6' : '#666')} 
                                    />
                                    <View style={styles.dayTabLabelBlock}>
                                        <Text style={[
                                            styles.dayTabDateText,
                                            activeTab === index + 1 && styles.activeDayTabText,
                                            isDisabled && styles.disabledDayTabDateText,
                                        ]}>
                                            {getTabDateLabel(date)}
                                        </Text>
                                        <Text style={[
                                            styles.dayTabSubText,
                                            activeTab === index + 1 && styles.activeDayTabSubText,
                                            isDisabled && styles.disabledDayTabSubText,
                                        ]}>
                                            Day {index + 1}
                                        </Text>
                                    </View>
                                </Pressable>
                            );
                        })}
                    </ScrollView>
                    
                    <Pressable style={styles.scrollArrow} onPress={scrollToNextDay}>
                        <MaterialCommunityIcons name="chevron-right" size={18} color="#666" />
                    </Pressable>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    tabsContainerWrapper: {
        backgroundColor: '#f8f9fa',
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
        paddingVertical: 16,
        paddingHorizontal: 16,
    },
    tabsRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    overviewTab: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        marginRight: 12,
        borderRadius: 8,
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#e9ecef',
        minWidth: 120,
    },
    activeOverviewTab: {
        backgroundColor: '#e3f2fd',
        borderColor: '#2C67D6',
    },
    overviewTabText: {
        fontSize: 14,
        color: '#666',
        marginLeft: 6,
        fontWeight: '500',
    },
    activeOverviewTabText: {
        color: '#2C67D6',
        fontWeight: '600',
    },
    dayTabsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        backgroundColor: 'white',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e9ecef',
        overflow: 'hidden',
    },
    scrollArrow: {
        padding: 12,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        borderRightWidth: 1,
        borderRightColor: '#e9ecef',
    },
    dayTabsScrollView: {
        flex: 1,
    },
    dayTabsContentContainer: {
        flexDirection: 'row',
        paddingHorizontal: 4,
        alignItems: 'center',
    },
    dayTab: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 14,
        paddingVertical: 9,
        marginHorizontal: 2,
        borderRadius: 6,
        backgroundColor: 'transparent',
        minWidth: 110,
        justifyContent: 'center',
    },
    dayTabLabelBlock: {
        marginLeft: 6,
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    dayTabDateText: {
        fontSize: 12,
        color: '#4B5563',
        fontWeight: '600',
        lineHeight: 15,
    },
    activeDayTab: {
        backgroundColor: '#e3f2fd',
        borderWidth: 1,
        borderColor: '#2C67D6',
    },
    dayTabSubText: {
        fontSize: 10,
        color: '#6B7280',
        fontWeight: '500',
        lineHeight: 13,
    },
    activeDayTabText: {
        color: '#2C67D6',
        fontWeight: '600',
    },
    activeDayTabSubText: {
        color: '#2C67D6',
        fontWeight: '600',
    },
    disabledDayTabDateText: {
        color: '#9CA3AF',
    },
    disabledDayTabSubText: {
        color: '#9CA3AF',
    },
    disabledDayTab: {
        opacity: 0.85,
    },
});

export default TabBar;

